// Model admin — Staff Plan Parser skill.
// One markdown skill per model, sent to the AI as its system prompt when a
// staff plan workbook is uploaded. Model-specific title / channel / location
// mappings live in the skill's Mappings section.

import { useState } from 'react';
import clsx from 'clsx';
import { Check, Copy, RotateCcw } from 'lucide-react';
import { DEFAULT_STAFF_PLAN_PARSER_SKILL, staffPlanParserSkill } from '@rc/engine';
import type { Model } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function StaffPlanParserEditor({ model, onChange }: Props) {
  const skill = staffPlanParserSkill(model);
  const isDefault = skill === DEFAULT_STAFF_PLAN_PARSER_SKILL;
  const [copied, setCopied] = useState(false);

  const setSkill = (next: string) =>
    onChange({ ...model, staffPlanParser: { ...model.staffPlanParser, skill: next } });

  const reset = () => {
    if (
      !isDefault &&
      !window.confirm('Replace this model’s parser skill with the default? Your edits will be lost.')
    ) {
      return;
    }
    setSkill(DEFAULT_STAFF_PLAN_PARSER_SKILL);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(skill);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked; the text is still selectable in the editor.
    }
  };

  const lines = skill.split('\n').length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-800">Parser skill</h3>
            <span
              className={clsx(
                'pill text-[10px] uppercase tracking-wider border',
                isDefault
                  ? 'bg-slate-100 text-slate-600 border-slate-200'
                  : 'bg-brand-purple-50 text-brand-purple-700 border-brand-purple-200',
              )}
            >
              {isDefault ? 'Default' : 'Customized'}
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Instructions the AI follows to turn an uploaded staff plan workbook into roster rows
            for Review. It receives this skill plus the model’s disciplines, channels, hubs and
            levels, so ids must match this model. Tune client wording in the{' '}
            <span className="font-medium text-slate-600">Mappings</span> section at the end.
            Not used by scenario calculations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={copy}>
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={reset}
            disabled={isDefault}
            title="Replace with the built-in default skill"
          >
            <RotateCcw size={13} /> Reset to default
          </button>
        </div>
      </div>

      <textarea
        className="input w-full font-mono text-xs leading-relaxed min-h-[560px] resize-y"
        spellCheck={false}
        value={skill}
        onChange={(e) => setSkill(e.target.value)}
        aria-label="Staff plan parser skill (markdown)"
      />
      <div className="text-[11px] text-slate-400 tabular-nums">
        Markdown · {lines.toLocaleString()} lines · {skill.length.toLocaleString()} characters
      </div>
    </div>
  );
}
